import {
  Alert,
  Avatar,
  Button,
  Descriptions,
  Form,
  Input,
  Modal,
  Typography,
} from "antd";
import { PictureOutlined } from "@ant-design/icons";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IProductsOrder } from "../../../models/order/products-order/response";
import { IReturnOrderProductResponse } from "../../../models/order-product/types";
import OrderProductService from "../../../services/order-product";
import { httpclient } from "../../../services/http-client";
import { getApiErrorDataMessage } from "../../../utils/api/get-api-error-message";
import { formatRecordReservedQuantityWithUnit } from "../../../utils/helpers/quantity";
import styles from "./ReturnProductForm.module.scss";

interface Props {
  line: IProductsOrder;
  expectedStatus: number;
  expectedFinalPrice: number;
  onCancel: () => void;
  /** Refresh the order only after the operator closes the successful result. */
  onSuccess: () => void;
}

export default function ReturnProductForm({
  line,
  expectedStatus,
  expectedFinalPrice,
  onCancel,
  onSuccess,
}: Props) {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const submitting = useRef(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<
    IReturnOrderProductResponse["data"] | null
  >(null);
  const product =
    line.isAlternative && line.alternativeProduct
      ? line.alternativeProduct
      : line.product;
  const productName = product?.name || "—";
  const quantity = formatRecordReservedQuantityWithUnit({ ...line, product });
  const formatMoney = (value: unknown) =>
    value !== null && value !== undefined && Number.isFinite(Number(value))
      ? `${Number(value).toLocaleString(i18n.language, { maximumFractionDigits: 2 })} ${t("currency")}`
      : "—";
  const close = () => {
    if (submitting.current) return;
    if (result) onSuccess();
    else onCancel();
  };
  const submit = async ({ reason }: { reason?: string }) => {
    if (submitting.current || result) return;
    submitting.current = true;
    setSaving(true);
    setErrorMessage(null);
    try {
      const response = await new OrderProductService(httpclient).returnProduct({
        productOrderId: line.id,
        reason: reason?.trim() || null,
        expectedStatus,
        expectedFinalPrice,
      });
      setResult(response.data.data);
    } catch (error: any) {
      setErrorMessage(
        error?.response
          ? getApiErrorDataMessage(
              error.response.data,
              t("customer_return_failed"),
            )
          : t("customer_return_connection_uncertain"),
      );
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={t(
        result ? "customer_return_result_title" : "customer_return_action",
      )}
      width={520}
      onCancel={close}
      closable={!saving}
      maskClosable={false}
      keyboard={!saving}
      footer={
        result ? (
          <Button type="primary" onClick={close}>
            {t("close")}
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={saving}>
              {t("cancel")}
            </Button>
            <Button
              type="primary"
              danger
              loading={saving}
              onClick={() => form.submit()}
            >
              {t("customer_return_confirm")}
            </Button>
          </>
        )
      }
    >
      <div className={styles.product}>
        <Avatar
          className={styles.image}
          shape="square"
          size={72}
          src={product?.image?.url}
          alt={productName}
          icon={<PictureOutlined />}
        />
        <div className={styles.identity}>
          <Typography.Title level={5} className={styles.name}>
            {productName}
          </Typography.Title>
          {line.isAlternative && line.product?.name && (
            <Typography.Text type="secondary">
              {t("alternative_to", { product: line.product.name })}
            </Typography.Text>
          )}
          <div>
            <Typography.Text type="secondary">
              {t(
                result
                  ? "customer_return_quantity_done"
                  : "customer_return_quantity",
              )}
              :
            </Typography.Text>{" "}
            <Typography.Text strong>{quantity}</Typography.Text>
          </div>
        </div>
      </div>
      {result ? (
        <div aria-live="polite">
          <Alert
            type="success"
            showIcon
            message={t("customer_return_success")}
            className={styles.notice}
          />
          <Descriptions column={1} size="small" className={styles.amounts}>
            <Descriptions.Item
              label={t(
                result.priceCorrection < 0
                  ? "customer_return_amount_increase"
                  : "customer_return_amount_reduced",
              )}
            >
              <Typography.Text strong className={styles.money}>
                {formatMoney(Math.abs(result.priceCorrection))}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label={t("customer_return_order_after")}>
              <Typography.Text strong className={styles.money}>
                {formatMoney(result.orderFinalPrice)}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        </div>
      ) : (
        <>
          <Descriptions column={1} size="small" className={styles.amounts}>
            <Descriptions.Item label={t("customer_return_product_value")}>
              <span className={styles.money}>
                {formatMoney(line.totalPriceOnClient)}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t("customer_return_order_before")}>
              <span className={styles.money}>
                {formatMoney(expectedFinalPrice)}
              </span>
            </Descriptions.Item>
          </Descriptions>
          <Typography.Paragraph type="secondary">
            {t("customer_return_final_amount_hint")}
          </Typography.Paragraph>
          <Alert
            type="info"
            showIcon
            message={t(
              expectedStatus === 8
                ? "customer_return_completed_hint"
                : "customer_return_delivery_hint",
            )}
            description={t("customer_return_inventory_hint")}
            className={styles.notice}
          />
          <Form
            form={form}
            layout="vertical"
            onFinish={submit}
            disabled={saving}
          >
            {errorMessage && (
              <Alert
                type="error"
                showIcon
                message={errorMessage}
                className={styles.notice}
              />
            )}
            <Form.Item
              name="reason"
              label={t("customer_return_reason")}
              rules={[{ max: 1000, message: t("customer_return_reason_long") }]}
            >
              <Input.TextArea autoFocus rows={2} maxLength={1000} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
